import { useCallback, useEffect, useState } from 'react';
import { crmApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { TablePagination } from '@/components/ui/TablePagination';
import { usePagination } from '@/hooks/usePagination';
import { usePermissions } from '@/hooks/usePermissions';
import type { CrmCampaign } from '@/types/crm';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
const SEGMENTS = [
  { value: '*', label: 'All customers' },
  { value: 'lead_source:WEB_FORM', label: 'Web form leads' },
  { value: 'lead_source:facebook_INBOX', label: 'Facebook inbox leads' },
];

const CHANNELS = ['EMAIL', 'SMS', 'WHATSAPP'];

export function CrmCampaignsPage() {
  const { hasAny } = usePermissions();
  const canManage = hasAny('CRM_MANAGE', 'CRM_CAMPAIGN_MANAGE');
  const pagination = usePagination();
  const [campaigns, setCampaigns] = useState<CrmCampaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [channel, setChannel] = useState('EMAIL');
  const [segmentQuery, setSegmentQuery] = useState('*');
  const [scheduleAt, setScheduleAt] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const page = await crmApi.listCampaigns({
        page: pagination.page,
        size: pagination.size,
      });
      setCampaigns(page.content);
      pagination.applyPageResponse(page);
    } catch (err) {
      showApiError(err, 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, pagination]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!name.trim()) return;
    try {
      await crmApi.createCampaign({ name, channel, segmentQuery, campaignStatus: 'DRAFT' });
      notify.success('Campaign created');
      setName('');
      load();
    } catch (err) {
      showApiError(err, 'Failed to create campaign');
    }
  };

  const launch = async (id: number) => {
    try {
      await crmApi.launchCampaign(id);
      notify.success('Campaign launched');
      load();
    } catch (err) {
      showApiError(err, 'Launch failed');
    }
  };

  const schedule = async (id: number) => {
    if (!scheduleAt) {
      notify.error('Pick a schedule date/time first');
      return;
    }
    try {
      await crmApi.scheduleCampaign(id, new Date(scheduleAt).toISOString().slice(0, 19));
      notify.success('Campaign scheduled');
      load();
    } catch (err) {
      showApiError(err, 'Schedule failed');
    }
  };

  return (
    <div>
      <PageHeader
        title="CRM campaigns"
        description="Segmented outreach with email, SMS, and WhatsApp channels"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'CRM', to: '/crm' },
          { label: 'Campaigns' },
        ]}
      />

      <div className="card p-4 mb-6 grid gap-3 md:grid-cols-2">
        <Input label="Campaign name" value={name} onChange={(e) => setName(e.target.value)} />
        <Select
          label="Channel"
          options={CHANNELS.map((c) => ({ value: c, label: c }))}
          value={channel}
          onChange={setChannel}
        />
        <Select
          label="Audience segment"
          className="md:col-span-2"
          options={SEGMENTS.map((s) => ({ value: s.value, label: s.label }))}
          value={segmentQuery}
          onChange={setSegmentQuery}
        />
        <div className="md:col-span-2">
          {canManage && <Button onClick={create}>Create campaign</Button>}
        </div>
        {canManage && (
          <Input
            label="Schedule time (for row actions below)"
            type="datetime-local"
            value={scheduleAt}
            onChange={(e) => setScheduleAt(e.target.value)}
            className="md:col-span-2"
          />
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500 border-b">
              <th className="py-2">Name</th>
              <th>Channel</th>
              <th>Segment</th>
              <th>Status</th>
              <th>Scheduled</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {campaigns.map((c) => (
              <tr key={c.id} className="border-b border-slate-100">
                <td className="py-2 font-medium">{c.name}</td>
                <td>{c.channel}</td>
                <td className="text-slate-600">{c.segmentQuery || '*'}</td>
                <td>{c.campaignStatus}</td>
                <td className="text-xs text-slate-500">{c.scheduledAt ? c.scheduledAt.slice(0, 16).replace('T', ' ') : '—'}</td>
                <td className="space-x-2">
                  {canManage && c.campaignStatus !== 'COMPLETED' && c.campaignStatus !== 'RUNNING' && (
                    <>
                      <Button variant="secondary" onClick={() => launch(c.id!)}>
                        Launch
                      </Button>
                      {c.campaignStatus === 'DRAFT' && (
                        <Button variant="secondary" onClick={() => schedule(c.id!)}>
                          Schedule
                        </Button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <TablePagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          totalElements={pagination.totalElements}
          onPageChange={pagination.setPage}
          loading={loading}
          hasRows={campaigns.length > 0}
        />
      </div>
    </div>
  );
}
